import moment from 'moment';
import { generateTimestampsInSeconds, toHHMMSS } from '@/util/date';

export function getIntervalSeconds(interval) {
  switch (interval) {
    case 'minute':
      return 60;
    case 'hour':
      return 60 * 60;
    case 'day':
      return 60 * 60 * 24;
    case 'week':
      return 60 * 60 * 24 * 7;
    default:
      return 60;
  }
}

function roundKeyByInterval(key, interval) {
  return moment(key * 1000).startOf(interval).unix();
}

function roundKeyLegacyHour(key, idx, interval) {
  let roundedKey = Math.round(key / 3600) * 3600;
  if (idx !== 0) roundedKey = moment(key * 1000).startOf(interval).unix();
  return roundedKey;
}

function buildRoundedMap(data, interval, roundingStrategy) {
  const rounded = {};
  Object.keys(data || {}).forEach((key, idx) => {
    const roundedKey = roundingStrategy === 'legacy-hour'
      ? roundKeyLegacyHour(parseInt(key, 10), idx, interval)
      : roundKeyByInterval(parseInt(key, 10), interval);
    rounded[roundedKey] = data[key];
  });
  return rounded;
}

export function buildFmsSeries({
  summary,
  dateRange,
  interval,
  includeTemperature = false,
  temperatureKey = 'temperatureData',
  roundingStrategy = 'interval',
  normalizeHourlyTimestamps = false,
  dataSort,
  intervalSecondsOverride,
}) {
  if (!summary) return [];

  const intervalSeconds = intervalSecondsOverride ?? getIntervalSeconds(interval);
  let timestamps = generateTimestampsInSeconds(
    moment(dateRange.start).unix(),
    moment(dateRange.end).unix(),
    intervalSeconds,
  );

  if (normalizeHourlyTimestamps) {
    timestamps = timestamps.map((timestamp, i) => {
      if (i === 0) return timestamp;
      return moment(timestamp * 1000).startOf('hour').unix();
    });
  }

  const roundedGps = buildRoundedMap(summary.gps?.data || {}, interval, roundingStrategy);
  const roundedRpm = buildRoundedMap(summary.rpm?.data || {}, interval, roundingStrategy);
  const roundedFm = buildRoundedMap(summary.flowmeter?.data || {}, interval, roundingStrategy);
  const roundedAe = buildRoundedMap(summary.ae?.data || {}, interval, roundingStrategy);
  const roundedTemperature = includeTemperature
    ? buildRoundedMap(summary.temperature?.data || {}, interval, roundingStrategy)
    : null;

  let mappedSeries = timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {
      STARBOARD: {},
      PORT: {},
    };
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const entry = {
      timestamp,
      gpsData,
      rpmData,
      fmData,
      aeData,
    };

    if (includeTemperature) {
      entry[temperatureKey] = roundedTemperature?.[timestamp] || {};
    }

    return entry;
  });

  if (dataSort === 'latest') {
    mappedSeries = mappedSeries.sort((a, b) => b.timestamp - a.timestamp);
  }

  if (dataSort === 'oldest') {
    mappedSeries = mappedSeries.sort((a, b) => a.timestamp - b.timestamp);
  }

  return mappedSeries;
}

export function buildFmsSummary({
  summary,
  dateRange,
  interval,
  includeFlowDetails = false,
  includeAeLastRunning = false,
  includeAeTotals = false,
  aeFuelFixed = false,
  includeTemperatureSummary = false,
  includeSeriesTemperature = false,
  seriesTemperatureKey = 'temperatureData',
  seriesRoundingStrategy = 'interval',
}) {
  if (!summary) return null;

  const mappedSummary = {};
  const intervalSeconds = getIntervalSeconds(interval);
  const unixRangeStart = moment(dateRange.start).unix();
  const unixRangeEnd = moment(dateRange.end).unix();
  const intervalCount = Math.ceil((unixRangeEnd - unixRangeStart) / intervalSeconds);

  const lastGpsData = Object.values(summary.gps?.data || [{}])[Object.values(summary.gps?.data || [{}]).length - 1];
  const gpsCount = Object.values(summary.gps?.data || []).length;

  mappedSummary.gps = {
    lastGpsData,
    averageSpeed: (summary.gps?.summary?.averageSpeed * 0.5399568034557236).toFixed(2),
    totalDistance: (summary.gps?.summary?.totalDistance / 1852).toFixed(2),
    lastData: lastGpsData ? moment(lastGpsData.timestamp * 1000).format('DD MMMM YYYY HH:mm') : '-',
    percentage: ((gpsCount / intervalCount) * 100).toFixed(1),
  };

  const lastPortRpmData = Object.values(summary.rpm?.data || []).length > 0
    ? Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1].PORT
    : {};
  const lastStarboardRpmData = Object.values(summary.rpm?.data || []).length > 0
    ? Object.values(summary.rpm?.data)[Object.values(summary.rpm?.data).length - 1].STARBOARD
    : {};

  const rpmCount = Object.values(summary.rpm?.data || []).length;

  mappedSummary.rpm = {
    port: {
      lastData: lastPortRpmData ? moment(lastPortRpmData.timestamp * 1000).format('DD MMMM YYYY HH:mm') : '-',
      runningTime: toHHMMSS(summary.rpm?.summary?.runningTime?.PORT || 0),
      average: (summary.rpm?.summary?.average?.PORT || 0).toFixed(2),
      lastRpmData: lastPortRpmData,
    },
    starboard: {
      lastData: lastStarboardRpmData ? moment(lastStarboardRpmData.timestamp * 1000).format('DD MMMM YYYY HH:mm') : '-',
      runningTime: toHHMMSS(summary.rpm?.summary?.runningTime?.STARBOARD || 0),
      average: (summary.rpm?.summary?.average?.STARBOARD || 0).toFixed(2),
      lastRpmData: lastStarboardRpmData,
    },
    percentage: ((rpmCount / intervalCount) * 100).toFixed(1),
  };

  const lastPortFmData = Object.values(summary?.flowmeter?.data || []).length > 0
    ? Object.values(summary.flowmeter.data)[Object.values(summary.flowmeter.data).length - 1]?.PORT
    : {};
  const lastStarboardFmData = Object.values(summary?.flowmeter?.data || []).length > 0
    ? Object.values(summary.flowmeter.data)[Object.values(summary.flowmeter.data).length - 1]?.STARBOARD
    : {};

  const fmCount = Object.values(summary?.flowmeter?.data || []).length;

  mappedSummary.fm = {
    port: {
      avgFuelFlow: (summary.flowmeter?.summary?.portAverageFlow || 0).toFixed(2),
      engineCons: (summary.flowmeter?.summary?.portEngineCons || 0).toFixed(2),
      lastData: lastPortFmData ? moment(lastPortFmData.timestamp * 1000).format('DD MMMM YYYY HH:mm') : '-',
      lastFmData: lastPortFmData,
    },
    starboard: {
      avgFuelFlow: (summary.flowmeter?.summary?.starboardAverageFlow || 0).toFixed(2),
      engineCons: (summary.flowmeter?.summary?.starboardEngineCons || 0).toFixed(2),
      lastData: lastStarboardFmData ? moment(lastStarboardFmData?.timestamp * 1000).format('DD MMMM YYYY HH:mm') : '-',
      lastFmData: lastStarboardFmData,
    },
    meEngineCons: (summary.flowmeter?.summary?.meEngineCons || 0).toFixed(2),
    percentage: ((fmCount / intervalCount) * 100).toFixed(1),
  };

  if (includeFlowDetails) {
    mappedSummary.fm.port.runningTime = toHHMMSS(summary.flowmeter?.summary?.PORT_IN?._flowTime || 0);
    mappedSummary.fm.port.avgDensity = (summary.flowmeter?.summary?.PORT_IN?._averageDens || 0).toFixed(2);
    mappedSummary.fm.starboard.runningTime = toHHMMSS(summary.flowmeter?.summary?.STARBOARD_IN?._flowTime || 0);
    mappedSummary.fm.starboard.avgDensity = (summary.flowmeter?.summary?.STARBOARD_IN?._averageDens || 0).toFixed(2);
  }

  const lastAeData = Object.values(summary?.ae?.data || []).length > 0
    ? Object.values(summary?.ae?.data)[Object.values(summary.ae?.data).length - 1]
    : null;
  const lastAeKey = Object.keys(summary?.ae?.data || []).length > 0
    ? Object.keys(summary?.ae?.data)[Object.keys(summary.ae?.data || [{}]).length - 1]
    : null;

  const aeCount = Object.keys(summary?.ae?.data || []).length;
  const formatAeFuel = (value) => (aeFuelFixed ? (value || 0).toFixed(2) : (value || '-'));

  mappedSummary.ae = {
    ae1: {
      fuelConsumption: formatAeFuel(summary.ae?.summary?.fuelConsumption?.AE1 || 0),
      runningSeconds: toHHMMSS(summary.ae?.summary?.runningSeconds?.AE1 || 0),
    },
    ae2: {
      fuelConsumption: formatAeFuel(summary.ae?.summary?.fuelConsumption?.AE2 || 0),
      runningSeconds: toHHMMSS(summary.ae?.summary?.runningSeconds?.AE2 || 0),
    },
    ae3: {
      fuelConsumption: formatAeFuel(summary.ae?.summary?.fuelConsumption?.AE3 || 0),
      runningSeconds: toHHMMSS(summary.ae?.summary?.runningSeconds?.AE3 || 0),
    },
    lastAeData,
    lastData: lastAeKey ? moment(lastAeKey * 1000).format('DD MMMM YYYY HH:mm') : '-',
    percentage: ((aeCount / intervalCount) * 100).toFixed(1),
  };

  if (includeAeLastRunning) {
    mappedSummary.ae.ae1.lastRunningTime = lastAeData?.AE1.runningTime || 0;
    mappedSummary.ae.ae2.lastRunningTime = lastAeData?.AE2.runningTime || 0;
    mappedSummary.ae.ae3.lastRunningTime = lastAeData?.AE3.runningTime || 0;
  }

  if (includeAeTotals) {
    mappedSummary.ae.runningSeconds = toHHMMSS(
      (summary.ae?.summary?.runningSeconds?.AE1 || 0)
      + (summary.ae?.summary?.runningSeconds?.AE2 || 0)
      + (summary.ae?.summary?.runningSeconds?.AE3 || 0),
    );
    mappedSummary.ae.fuelConsumption = (
      (summary.ae?.summary?.fuelConsumption?.AE1 || 0)
      + (summary.ae?.summary?.fuelConsumption?.AE2 || 0)
      + (summary.ae?.summary?.fuelConsumption?.AE3 || 0)
    ).toFixed(2);
  }

  if (includeTemperatureSummary) {
    mappedSummary.temperature = {
      exhaust: {
        port: {
          average: summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE?.PORT?.[0]?.avg?.toFixed(2) || '-',
        },
        starboard: {
          average: summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE?.STARBOARD?.[0]?.avg?.toFixed(2) || '-',
        },
      },
      tank: {
        port: {
          average: summary?.temperature?.summary?.tempDetails?.TEMPERATURE?.PORT?.[0]?.avg?.toFixed(2) || '-',
        },
        starboard: {
          average: summary?.temperature?.summary?.tempDetails?.TEMPERATURE?.STARBOARD?.[0]?.avg?.toFixed(2) || '-',
        },
      },
    };
  }

  mappedSummary.series = buildFmsSeries({
    summary,
    dateRange,
    interval,
    includeTemperature: includeSeriesTemperature,
    temperatureKey: seriesTemperatureKey,
    roundingStrategy: seriesRoundingStrategy,
  });

  return mappedSummary;
}
